(() => {
  document.documentElement.dataset.boiSearchRuntime = "booting";
  if (window.__boiStorefrontSearchLoaded) return;
  window.__boiStorefrontSearchLoaded = true;

  const API_FILTER_PARAMS = [
    "producer",
    "category",
    "region",
    "originCountry",
    "denomination",
    "grape",
    "format",
    "vintage",
    "wineType",
    "availability",
  ];
  const RANGE_FILTER_PARAMS = ["priceMin", "priceMax", "alcoholMin", "alcoholMax"];
  const ALL_FILTER_PARAMS = [...API_FILTER_PARAMS, ...RANGE_FILTER_PARAMS];
  const FACET_PARAMS = {
    producer: "producer",
    region: "region",
    originCountry: "originCountry",
    denomination: "denomination",
    grape: "grape",
    format: "format",
    vintage: "vintage",
    wine_type: "wineType",
    availability: "availability",
  };
  const DEFAULT_I18N = {
    suggestions: "Popular suggestions",
    categories: "Categories",
    collections: "Collections",
    pages: "Pages",
    products: "Products",
    no_results: "No results",
    view_all: "View all results",
    view_all_template: "View all {count} products",
    filters: "Filters",
    filter_results: "Filter results",
    close: "Close",
    clear_filters: "Clear all",
    apply: "Apply",
    price_min_placeholder: "Min price",
    price_max_placeholder: "Max price",
    alcohol_min_placeholder: "Min alcohol",
    alcohol_max_placeholder: "Max alcohol",
    status_template: "{count} results for “{query}”",
    did_you_mean: "Did you mean",
    sort: "Sort results",
    sort_relevance: "Relevance",
    sort_price_asc: "Price: low to high",
    sort_price_desc: "Price: high to low",
    sort_newest: "Newest",
    facets_aria: "Search filters",
    pagination_aria: "Search pages",
    previous_page: "Previous page",
    next_page: "Next page",
    remove_filter: "Remove {value}",
    in_stock: "In stock",
    backorder: "Available to order",
    out_of_stock: "Out of stock",
    view_product: "View product",
    add_to_cart: "Add to cart",
    quantity: "Quantity",
    sold_out: "Sold out",
    show_more: "Show more",
    show_less: "Show less",
    filter_count_template: "Filters ({count})",
    show_results_template: "Show {count} products",
    award: "Award-winning product",
    marketplace: "Marketplace product",
    fast_shipping: "Fast shipping",
    sale: "Save {percent}%",
    loading: "Loading search results…",
    error: "Search is temporarily unavailable. Native Shopify search remains available.",
    retry: "Try again",
    vat_exclusive: "VAT excl.",
    vat_inclusive: "VAT incl.",
    b2b_unavailable: "B2B pricing is currently unavailable. Use Shopify search.",
    facet_producer: "Producer",
    facet_region: "Region",
    facet_originCountry: "Country",
    facet_denomination: "Denomination",
    facet_grape: "Grape",
    facet_format: "Format",
    facet_vintage: "Vintage",
    facet_wineType: "Wine type",
    facet_availability: "Availability",
    facet_price: "Price",
    facet_alcohol: "Alcohol",
  };
  const CACHE_TTL = 30_000;
  const responseCache = new Map();
  const b2bContextPromises = new Map();

  const translations = (host) => {
    let extra = {};
    try {
      if (host.dataset.i18n) extra = JSON.parse(host.dataset.i18n);
    } catch {
      extra = {};
    }
    const translated = { ...DEFAULT_I18N, ...extra };
    if (host.dataset.locale === "it") {
      translated.vat_exclusive = "IVA Escl.";
      translated.vat_inclusive = "IVA Incl.";
    }
    if (host.dataset.locale === "fr") {
      translated.vat_exclusive = "TVA excl.";
      translated.vat_inclusive = "TVA incl.";
    }
    return translated;
  };
  const formatTemplate = (template, values) =>
    template.replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match,
    );
  const debounce = (fn, wait) => {
    let timer;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => clearTimeout(timer);
    return debounced;
  };
  const uuid = () => {
    try {
      return crypto.randomUUID();
    } catch {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        return (character === "x" ? random : (random & 0x3) | 0x8).toString(16);
      });
    }
  };
  let sessionId = (() => {
    const key = "boi-search-session";
    try {
      let value = sessionStorage.getItem(key);
      if (!value) {
        value = uuid();
        sessionStorage.setItem(key, value);
      }
      return value;
    } catch {
      return uuid();
    }
  })();
  let activeB2BPartition = null;
  const directQuickAddVariants = new Set();
  const ATTRIBUTION_STORAGE_KEY = "boi-search-attribution-v1";
  const attributionRecords = () => {
    try {
      const records = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY) || "[]");
      if (!Array.isArray(records)) return [];
      const active = records.filter(
        (record) => record?.expiresAt > Date.now() && record.searchId && record.productId,
      );
      if (active.length !== records.length)
        localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(active));
      return active;
    } catch {
      return [];
    }
  };
  const saveAttributionRecord = (result, product) => {
    if (!result?.attributionToken || !result.searchId || !product?.productId) return;
    const numericProductId = numericShopifyId(product.productId);
    if (!numericProductId) return;
    try {
      const records = attributionRecords().filter(
        (record) => String(record.productNumericId) !== numericProductId,
      );
      records.push({
        eventId: uuid(),
        searchId: result.searchId,
        attributionToken: result.attributionToken,
        productId: product.productId,
        productNumericId: numericProductId,
        expiresAt: Date.now() + 7 * 86_400_000,
      });
      localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(records.slice(-20)));
    } catch {
      // Attribution remains best-effort when browser storage is unavailable.
    }
  };
  const money = (value, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
    } catch {
      return `${Number(value).toFixed(2)} ${currency}`;
    }
  };
  const rootPath = (root) => {
    const normalized = root || "/";
    return normalized.endsWith("/") ? normalized : `${normalized}/`;
  };
  const productUrl = (root, handle) =>
    `${rootPath(root)}products/${encodeURIComponent(handle)}`.replace(/\/+/g, "/");
  const searchUrl = (root, query) => {
    const path = `${rootPath(root)}search`.replace(/\/+/g, "/");
    return `${path}?q=${encodeURIComponent(query)}`;
  };
  const safePath = (path) =>
    typeof path === "string" && path.startsWith("/") && !path.startsWith("//") ? path : null;
  const localizedNavigationPath = (item, root) => {
    const localized = safePath(item.localizedPath);
    if (localized) return localized;
    const path = safePath(item.path);
    if (!path) return rootPath(root);
    const prefix = rootPath(root).replace(/\/$/, "");
    if (!prefix || path === prefix || path.startsWith(`${prefix}/`)) return path;
    return `${prefix}${path}`;
  };
  const element = (tag, attrs = {}, text) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value === null || value === undefined || value === false) continue;
      if (key === "class") node.className = value;
      else if (key === "dataset") Object.assign(node.dataset, value);
      else if (key === "htmlFor") node.htmlFor = value;
      else if (key === "selected") node.selected = Boolean(value);
      else if (key === "checked") node.checked = Boolean(value);
      else if (key.startsWith("aria-") || key === "role") node.setAttribute(key, String(value));
      else node[key] = value;
    }
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const cacheKey = (base, path) => `${base.replace(/\/$/, "")}${path}`;
  const safePartition = (value) => {
    let hash = 2166136261;
    for (const character of String(value)) {
      hash ^= character.charCodeAt(0);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
  };
  const contextFor = async (host) => {
    if (host?.dataset.b2b !== "true") return { audience: "retail", token: "" };
    const locationId = host.boiCompanyLocationId || host.dataset.companyLocationId;
    host.boiCompanyLocationId = locationId;
    host.removeAttribute("data-company-location-id");
    if (!locationId || !host.dataset.contextUrl) throw new Error("B2B context unavailable");
    const partition = safePartition(locationId);
    try {
      const storedPartition = sessionStorage.getItem("boi-b2b-location-partition");
      if (
        (storedPartition && storedPartition !== partition) ||
        (activeB2BPartition && activeB2BPartition !== partition)
      ) {
        sessionId = uuid();
        sessionStorage.setItem("boi-search-session", sessionId);
        responseCache.clear();
        b2bContextPromises.clear();
      }
      sessionStorage.setItem("boi-b2b-location-partition", partition);
    } catch {
      if (activeB2BPartition && activeB2BPartition !== partition) {
        sessionId = uuid();
        responseCache.clear();
        b2bContextPromises.clear();
      }
    }
    activeB2BPartition = partition;
    if (!b2bContextPromises.has(partition))
      b2bContextPromises.set(
        partition,
        (async () => {
          const storageKey = `boi-b2b-context:${partition}`;
          try {
            const cached = JSON.parse(sessionStorage.getItem(storageKey) || "null");
            if (cached?.token && cached.expiresAt > Date.now() + 30_000)
              return { audience: "b2b", token: cached.token, expiresAt: cached.expiresAt };
          } catch {
            sessionStorage.removeItem(storageKey);
          }
          const response = await fetch(host.dataset.contextUrl, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "content-type": "application/json",
              "x-search-session": sessionId,
            },
            body: JSON.stringify({ companyLocationId: locationId }),
          });
          if (!response.ok) throw new Error("B2B context unavailable");
          const value = await response.json();
          if (!value?.token) throw new Error("B2B context unavailable");
          try {
            sessionStorage.setItem(
              storageKey,
              JSON.stringify({
                token: value.token,
                expiresAt: Date.now() + value.expiresIn * 1000,
              }),
            );
          } catch {
            // The in-memory promise remains session-scoped when storage is unavailable.
          }
          return {
            audience: "b2b",
            token: value.token,
            expiresAt: Date.now() + value.expiresIn * 1000,
          };
        })(),
      );
    const context = await b2bContextPromises.get(partition);
    if (context.expiresAt <= Date.now() + 30_000) {
      b2bContextPromises.delete(partition);
      return contextFor(host);
    }
    return context;
  };
  const request = async (host, path, signal) => {
    const context = await contextFor(host);
    const base = host.dataset.apiBase;
    const key = `${cacheKey(base, path)}|${context.audience}:${safePartition(context.token)}`;
    const cached = responseCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.value;
    const response = await fetch(cacheKey(base, path), {
      signal,
      credentials: "omit",
      headers: {
        "x-search-session": sessionId,
        "x-bottle-search-audience": context.audience,
        ...(context.token ? { "x-bottle-search-context": context.token } : {}),
      },
    });
    if (!response.ok) throw new Error(`Search ${response.status}`);
    const value = await response.json();
    responseCache.set(key, { value, expiresAt: Date.now() + CACHE_TTL });
    if (responseCache.size > 40) responseCache.delete(responseCache.keys().next().value);
    return value;
  };
  const EU_VAT_COUNTRIES = new Set([
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "HU",
    "IE",
    "IT",
    "LT",
    "LU",
    "LV",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SE",
    "SI",
    "SK",
  ]);
  const priceDisplayLines = (host, data, product) => {
    const audience = data.pricingContext?.audience === "b2b" ? "B2B" : "RETAIL";
    const country = data.pricingContext?.countryCode || host.dataset.country;
    const taxesIncluded = host.dataset.taxesIncluded === "true";
    const canonical = Number(product.price.min);
    const hasVatLabel = EU_VAT_COUNTRIES.has(country);
    if (!hasVatLabel) return [{ amount: canonical, label: "" }];
    if (audience === "B2B" && country === "IT" && taxesIncluded) {
      const canonicalCents = Math.round(canonical * 100);
      return [
        { amount: Math.round((canonicalCents * 100) / 122) / 100, label: host.t.vat_exclusive },
        { amount: canonical, label: host.t.vat_inclusive },
      ];
    }
    return [
      {
        amount: canonical,
        label: audience === "RETAIL" ? host.t.vat_inclusive : host.t.vat_exclusive,
      },
    ];
  };
  const appendPriceDisplay = (container, host, data, product, className) => {
    for (const line of priceDisplayLines(host, data, product)) {
      const row = element("span", { class: className });
      row.append(
        element(
          "span",
          { class: "money" },
          money(line.amount, product.price.currency, host.dataset.locale),
        ),
      );
      if (line.label) row.append(element("small", { class: "boi-search-card__vat" }, line.label));
      container.append(row);
    }
  };
  const sendProductClick = (api, result, product, position, surface) => {
    if (!result?.analyticsToken || !result.searchId || !product?.productId) return;
    saveAttributionRecord(result, product);
    const body = JSON.stringify({
      eventId: uuid(),
      searchId: result.searchId,
      analyticsToken: result.analyticsToken,
      productId: product.productId,
      position,
      surface,
    });
    const endpoint = `${api.replace(/\/$/, "")}/v1/analytics/click`;
    if (navigator.sendBeacon) {
      // text/plain is CORS-safelisted, so navigation cannot strand a JSON preflight.
      const sent = navigator.sendBeacon(endpoint, body);
      if (sent) return;
    }
    void fetch(endpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  };
  const inventoryLabel = (value, t) => t[value] || value;
  const localeNumber = (value, locale, maximumFractionDigits = 1) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits }).format(value);
  const formatVolume = (value, locale) => {
    const match = /^\s*(\d+(?:[.,]\d+)?)\s*ml\s*$/iu.exec(String(value));
    if (!match) return String(value);
    return `${localeNumber(Number(match[1].replace(",", ".")) / 10, locale)} cl`;
  };
  const titleCaseTechnicalValue = (value, locale) => {
    const text = String(value).trim();
    if (!text || text !== text.toLocaleUpperCase(locale) || !/\p{L}/u.test(text)) return text;
    return `${text.charAt(0).toLocaleUpperCase(locale)}${text.slice(1).toLocaleLowerCase(locale)}`;
  };
  const facetValueLabel = (param, value, t, locale) => {
    if (param === "format") return formatVolume(value, locale);
    if (param === "availability") return inventoryLabel(value, t);
    if (["originCountry", "wineType", "region"].includes(param))
      return titleCaseTechnicalValue(value, locale);
    return String(value);
  };
  const numericShopifyId = (value) => /(?:^|\/)(\d+)$/.exec(String(value || ""))?.[1] || "";
  const discountPercent = (product) => {
    const compare = product.price?.compareAtMin;
    const price = product.price?.min;
    return compare > price && compare > 0 ? Math.round(((compare - price) / compare) * 100) : 0;
  };
  const promotionFor = (host, product, b2b) => {
    if (!host.promoRules) {
      try {
        host.promoRules = JSON.parse(host.dataset.promoConfig || "[]");
      } catch {
        host.promoRules = [];
      }
    }
    const tags = new Set(product.tags || []);
    return (
      host.promoRules
        .filter((promotion) => {
          if (!promotion?.tag || !tags.has(promotion.tag)) return false;
          const visibility = promotion.visibility || "all";
          return (
            visibility === "all" ||
            (b2b && visibility === "b2b_only") ||
            (!b2b && visibility === "non_b2b_only")
          );
        })
        .sort((left, right) => Number(left.priority) - Number(right.priority))[0] || null
    );
  };
  const promotionPrice = (canonical, promotion, b2b) => {
    if (!promotion || promotion.type === "case_quantity_discount") return canonical;
    if (promotion.type === "fixed_amount") {
      const retailFixed = Number(promotion.fixedAmount || 0);
      const b2bFixed = Number(promotion.fixedAmountB2b || 0);
      const fixed = b2b
        ? b2bFixed > 0
          ? b2bFixed
          : Math.round((retailFixed / 1.22) * 100) / 100
        : retailFixed;
      return Math.max(0, Math.round((canonical - fixed) * 100) / 100);
    }
    return Math.max(
      0,
      Math.round((canonical * 100 * (100 - Number(promotion.percent || 0))) / 100) / 100,
    );
  };
  const facetLabel = (facet, t) => {
    if (facet.key === "price") return t.facet_price;
    if (facet.key === "alcohol_by_volume") return t.facet_alcohol;
    const param = FACET_PARAMS[facet.key];
    return (param && t[`facet_${param}`]) || facet.label;
  };

  class BottleAutocomplete extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.dataset.apiBase) return;
      this.initialized = true;
      this.dataset.runtimeState = "connecting";
      this.t = translations(this);
      this.instanceId = uuid();
      this.form = this.resolveForm();
      this.input = this.resolveInput();
      if (!this.form || !this.input) {
        this.dataset.runtimeState = "waiting-for-form";
        this.initialized = false;
        this.retryCount = (this.retryCount || 0) + 1;
        if (this.retryCount <= 20)
          this.retryTimer = window.setTimeout(() => this.connectedCallback(), 250);
        return;
      }
      this.panel = element("div", {
        class: "boi-autocomplete",
        role: "listbox",
        hidden: true,
        id: `boi-list-${this.instanceId}`,
        "aria-label": this.t.suggestions,
      });
      document.body.append(this.panel);
      this.lifecycleController = new AbortController();
      const lifecycleSignal = this.lifecycleController.signal;
      this.input.setAttribute("role", "combobox");
      this.input.setAttribute("aria-autocomplete", "list");
      this.input.setAttribute("aria-controls", this.panel.id);
      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("autocomplete", "off");
      this.input.dataset.boiSearchInput = "true";
      this.input.classList.add("snize-exclude-input");
      this.form.dataset.boiSearchOwned = "true";
      this.form.dataset.boiSearchForm = "true";
      this.searchAction = searchUrl(this.dataset.rootUrl, "").split("?")[0];
      this.form.setAttribute("action", this.searchAction);
      this.formObserver = new MutationObserver(() => {
        if (this.form.getAttribute("action") !== this.searchAction)
          this.form.setAttribute("action", this.searchAction);
      });
      this.formObserver.observe(this.form, { attributes: true, attributeFilter: ["action"] });
      this.inputObserver = new MutationObserver(() => {
        if (!this.input.classList.contains("snize-exclude-input"))
          this.input.classList.add("snize-exclude-input");
        if (this.input.getAttribute("role") !== "combobox")
          this.input.setAttribute("role", "combobox");
        if (this.input.getAttribute("aria-controls") !== this.panel.id)
          this.input.setAttribute("aria-controls", this.panel.id);
        const expanded = String(!this.panel.hidden);
        if (this.input.getAttribute("aria-expanded") !== expanded)
          this.input.setAttribute("aria-expanded", expanded);
      });
      this.inputObserver.observe(this.input, {
        attributes: true,
        attributeFilter: ["class", "role", "aria-controls", "aria-expanded"],
      });
      this.options = [];
      this.selected = -1;
      this.sequence = 0;
      this.controller = null;
      this.search = debounce(() => this.performSearch(), 150);
      this.input.addEventListener(
        "input",
        (event) => {
          event.stopImmediatePropagation();
          this.search();
        },
        { capture: true, signal: lifecycleSignal },
      );
      for (const eventName of ["keyup", "change"])
        this.input.addEventListener(eventName, (event) => event.stopImmediatePropagation(), {
          capture: true,
          signal: lifecycleSignal,
        });
      this.input.addEventListener("keydown", (event) => this.onKeydown(event), {
        capture: true,
        signal: lifecycleSignal,
      });
      this.form.addEventListener(
        "submit",
        (event) => {
          if (this.selected >= 0 && this.options[this.selected]) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.options[this.selected].click();
            return;
          }
          const query = this.input.value.trim();
          if (!query) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitQuery(query);
        },
        { capture: true, signal: lifecycleSignal },
      );
      this.form.addEventListener(
        "click",
        (event) => {
          const submitter = event.target.closest?.("button[type='submit'],input[type='submit']");
          if (!submitter || !this.form.contains(submitter)) return;
          const query = this.input.value.trim();
          if (!query) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.submitQuery(query);
        },
        { capture: true, signal: lifecycleSignal },
      );
      document.addEventListener(
        "pointerdown",
        (event) => {
          if (!this.form.contains(event.target) && !this.panel.contains(event.target)) this.close();
        },
        { signal: lifecycleSignal },
      );
      document.addEventListener(
        "focusin",
        (event) => {
          if (!this.form.contains(event.target) && !this.panel.contains(event.target)) this.close();
        },
        { signal: lifecycleSignal },
      );
      window.addEventListener("resize", () => this.placePanel(), {
        passive: true,
        signal: lifecycleSignal,
      });
      window.addEventListener("scroll", () => this.placePanel(), {
        passive: true,
        signal: lifecycleSignal,
      });
      this.dataset.runtimeState = "ready";
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        window.clearTimeout(this.retryTimer);
        this.search?.cancel();
        this.controller?.abort();
        this.lifecycleController?.abort();
        this.inputObserver?.disconnect();
        this.formObserver?.disconnect();
        this.panel?.remove();
        this.initialized = false;
      });
    }

    resolveForm() {
      if (this.previousElementSibling?.matches("form")) return this.previousElementSibling;
      if (this.dataset.formSelector) return document.querySelector(this.dataset.formSelector);
      const input =
        this.dataset.inputSelector && document.querySelector(this.dataset.inputSelector);
      if (input?.form) return input.form;
      if (this.previousElementSibling?.matches("form")) return this.previousElementSibling;
      return this.closest("form");
    }

    resolveInput() {
      if (this.dataset.inputSelector) {
        const adjacent = this.previousElementSibling?.matches("form")
          ? this.previousElementSibling.querySelector("input[name='q']")
          : null;
        return adjacent || document.querySelector(this.dataset.inputSelector);
      }
      return this.form?.querySelector("input[name='q']");
    }

    submitQuery(query) {
      if (this.submitting) return;
      this.submitting = true;
      this.close();
      window.location.assign(searchUrl(this.dataset.rootUrl, query));
    }

    placePanel() {
      if (!this.panel || this.panel.hidden || !this.form) return;
      const rect = this.form.getBoundingClientRect();
      this.panel.style.setProperty("--boi-autocomplete-top", `${Math.max(0, rect.bottom + 14)}px`);
      if (matchMedia("(min-width: 901px)").matches) {
        const gutter = 16;
        const width = Math.min(1120, innerWidth - gutter * 2);
        const left = Math.min(
          innerWidth - width - gutter,
          Math.max(gutter, rect.left + rect.width / 2 - width / 2),
        );
        this.panel.style.setProperty("--boi-autocomplete-left", `${left}px`);
        this.panel.style.setProperty("--boi-autocomplete-width", `${width}px`);
      } else {
        this.panel.style.removeProperty("--boi-autocomplete-left");
        this.panel.style.removeProperty("--boi-autocomplete-width");
      }
    }

    close({ restoreFocus = false } = {}) {
      this.controller?.abort();
      this.search?.cancel();
      if (!this.panel) return;
      this.panel.hidden = true;
      document.documentElement.classList.remove("boi-search-overlay-open");
      this.input.setAttribute("aria-expanded", "false");
      this.input.removeAttribute("aria-activedescendant");
      this.selected = -1;
      if (restoreFocus) this.input.focus({ preventScroll: true });
    }

    choose(index) {
      if (!this.options.length) return;
      const next = (index + this.options.length) % this.options.length;
      this.options.forEach((node, optionIndex) =>
        node.setAttribute("aria-selected", String(optionIndex === next)),
      );
      this.selected = next;
      this.input.setAttribute("aria-activedescendant", this.options[next].id);
      this.options[next].scrollIntoView({ block: "nearest" });
    }

    onKeydown(event) {
      if (event.key === "Escape" && !this.panel.hidden) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.close({ restoreFocus: true });
        return;
      }
      if (this.panel.hidden) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.choose(this.selected + 1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.choose(this.selected - 1);
      } else if (event.key === "Enter" && this.selected >= 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.options[this.selected].click();
      } else if (event.key === "Enter") {
        const query = this.input.value.trim();
        if (!query) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.submitQuery(query);
      }
    }

    registerOption(link) {
      link.id = `boi-option-${this.instanceId}-${this.options.length}`;
      link.setAttribute("role", "option");
      link.setAttribute("aria-selected", "false");
      link.addEventListener("click", () => this.close());
      this.options.push(link);
      return link;
    }

    productCard(product, index, data) {
      const link = this.registerOption(
        element("a", {
          class: "boi-autocomplete__product",
          href: productUrl(this.dataset.rootUrl, product.handle),
        }),
      );
      const media = element("span", { class: "boi-autocomplete__product-media" });
      if (product.imageUrl)
        media.append(
          element("img", {
            src: product.imageUrl,
            alt: "",
            loading: index < 3 ? "eager" : "lazy",
            width: 120,
            height: 190,
          }),
        );
      media.append(
        element(
          "span",
          { class: `boi-autocomplete__stock boi-autocomplete__stock--${product.availability}` },
          inventoryLabel(product.availability, this.t),
        ),
      );
      const details = element("span", { class: "boi-autocomplete__product-details" });
      if (product.producer || product.vintage) {
        const metadata = element("span", { class: "boi-autocomplete__producer" });
        if (product.vintage)
          metadata.append(
            element("strong", { class: "boi-autocomplete__vintage" }, product.vintage),
          );
        if (product.producer) metadata.append(element("span", {}, product.producer));
        details.append(metadata);
      }
      details.append(element("span", { class: "boi-autocomplete__product-title" }, product.title));
      const b2b = data.pricingContext?.audience === "b2b";
      const promotion = promotionFor(this, product, b2b);
      if (promotion?.banner) {
        const promoBanner = element("span", { class: "boi-autocomplete__promo" }, promotion.banner);
        if (promotion.background) promoBanner.style.backgroundColor = String(promotion.background);
        if (promotion.color) promoBanner.style.color = String(promotion.color);
        details.append(promoBanner);
      }
      const price = element("span", { class: "boi-autocomplete__price" });
      const promotionalAmount = promotionPrice(Number(product.price.min), promotion, b2b);
      const priceProduct =
        promotion && promotion.type !== "case_quantity_discount"
          ? { ...product, price: { ...product.price, min: promotionalAmount } }
          : product;
      appendPriceDisplay(price, this, data, priceProduct, "boi-autocomplete__price-line");
      if (promotion && promotion.type !== "case_quantity_discount")
        price.append(
          element("del", {}, money(product.price.min, product.price.currency, this.dataset.locale)),
        );
      else if (!b2b && product.price.compareAtMin > product.price.min)
        price.append(
          element(
            "del",
            {},
            money(product.price.compareAtMin, product.price.currency, this.dataset.locale),
          ),
        );
      details.append(price);
      link.append(media, details);
      link.addEventListener("click", () =>
        sendProductClick(this.dataset.apiBase, data, product, index + 1, "autocomplete"),
      );
      return link;
    }

    render(data, query) {
      this.panel.replaceChildren();
      this.options = [];
      this.selected = -1;
      const productLimit = matchMedia("(max-width: 900px)").matches ? 4 : 6;
      const products = Array.isArray(data.products) ? data.products.slice(0, productLimit) : [];
      const suggestions = Array.isArray(data.suggestions) ? data.suggestions.slice(0, 7) : [];
      const navigation = Array.isArray(data.navigation) ? data.navigation.slice(0, 7) : [];
      if (!products.length && !suggestions.length && !navigation.length) {
        this.panel.append(
          element("div", { class: "boi-autocomplete__status", role: "status" }, this.t.no_results),
        );
      } else {
        const layout = element("div", { class: "boi-autocomplete__layout" });
        const discovery = element("div", { class: "boi-autocomplete__discovery" });
        if (suggestions.length) {
          const group = element("section", { class: "boi-autocomplete__group", role: "group" });
          group.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.suggestions));
          suggestions.forEach((item) => {
            const link = this.registerOption(
              element(
                "a",
                {
                  class: "boi-autocomplete__suggestion",
                  href: searchUrl(this.dataset.rootUrl, item.label),
                },
                item.label,
              ),
            );
            group.append(link);
          });
          discovery.append(group);
        }
        if (navigation.length) {
          const group = element("section", { class: "boi-autocomplete__group", role: "group" });
          group.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.categories));
          navigation.forEach((item) => {
            const heading = item.resourceType === "page" ? this.t.pages : this.t.collections;
            const link = this.registerOption(
              element("a", {
                class: "boi-autocomplete__navigation",
                href: localizedNavigationPath(item, this.dataset.rootUrl),
                "aria-label": `${item.title}, ${heading}`,
              }),
            );
            link.append(element("span", {}, item.title), element("small", {}, heading));
            group.append(link);
          });
          discovery.append(group);
        }
        const productArea = element("section", {
          class: "boi-autocomplete__products",
          role: "group",
        });
        productArea.append(element("h2", { class: "boi-autocomplete__heading" }, this.t.products));
        const grid = element("div", { class: "boi-autocomplete__product-grid" });
        products.forEach((product, index) => grid.append(this.productCard(product, index, data)));
        productArea.append(grid);
        const total = Number.isFinite(Number(data.total)) ? Number(data.total) : products.length;
        const viewAll = this.registerOption(
          element(
            "a",
            { class: "boi-autocomplete__view-all", href: searchUrl(this.dataset.rootUrl, query) },
            `${formatTemplate(this.t.view_all_template, { count: total })} →`,
          ),
        );
        productArea.append(viewAll);
        layout.append(productArea, discovery);
        this.panel.append(layout);
      }
      this.panel.hidden = false;
      document.documentElement.classList.add("boi-search-overlay-open");
      this.input.setAttribute("aria-controls", this.panel.id);
      this.input.setAttribute("aria-expanded", "true");
      this.placePanel();
    }

    async performSearch() {
      const query = this.input.value.trim();
      const sequence = ++this.sequence;
      if (query.length < 2) {
        this.close();
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      try {
        const data = await request(
          this,
          `/v1/autocomplete?q=${encodeURIComponent(query)}&locale=${encodeURIComponent(this.dataset.locale)}&marketCountry=${encodeURIComponent(this.dataset.country)}&limit=6`,
          this.controller.signal,
        );
        if (sequence === this.sequence && query === this.input.value.trim())
          this.render(data, query);
      } catch (error) {
        if (error.name !== "AbortError" && sequence === this.sequence) this.close();
      }
    }
  }

  class BottleResults extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.dataset.apiBase) return;
      this.initialized = true;
      this.abort = null;
      this.sequence = 0;
      this.t = translations(this);
      const native =
        this.dataset.nativeResultsSelector &&
        document.querySelector(this.dataset.nativeResultsSelector);
      if (native) native.hidden = true;
      const query = this.params().get("q")?.trim() || "";
      const restored = history.state?.boiSearch;
      if (restored?.journeyId && restored.query === query) {
        this.journeyId = restored.journeyId;
        this.nextInteractionKind = "RETRY";
      } else {
        this.journeyId = uuid();
        this.nextInteractionKind = "INTENT";
        history.replaceState(
          { ...(history.state || {}), boiSearch: { journeyId: this.journeyId, query } },
          "",
        );
      }
      this.onPopState = (event) => {
        const restoredJourney = event.state?.boiSearch;
        const currentQuery = this.params().get("q")?.trim() || "";
        this.journeyId =
          restoredJourney?.journeyId && restoredJourney.query === currentQuery
            ? restoredJourney.journeyId
            : uuid();
        this.nextInteractionKind = "RETRY";
        this.load();
      };
      window.addEventListener("popstate", this.onPopState);
      this.onDocumentPointerDown = (event) => {
        if (event.target.closest?.(".boi-search-card .qty-product--collection")) return;
        this.querySelectorAll(".qty-grid.open").forEach((grid) => grid.classList.remove("open"));
        this.querySelectorAll(".qty-product--collection.qty-open").forEach((widget) =>
          widget.classList.remove("qty-open"),
        );
      };
      document.addEventListener("pointerdown", this.onDocumentPointerDown);
      this.load();
    }

    disconnectedCallback() {
      this.abort?.abort();
      window.removeEventListener("popstate", this.onPopState);
      document.removeEventListener("pointerdown", this.onDocumentPointerDown);
    }

    params() {
      return new URL(location.href).searchParams;
    }

    apiParams() {
      const source = this.params();
      const params = new URLSearchParams();
      const query = source.get("q")?.trim();
      if (query) params.set("q", query);
      for (const key of ["page", "sort", ...ALL_FILTER_PARAMS])
        source.getAll(key).forEach((value) => {
          if (value) params.append(key, value);
        });
      params.set("locale", this.dataset.locale);
      params.set("marketCountry", this.dataset.country);
      params.set("perPage", this.dataset.perPage || "24");
      params.set("journeyId", this.journeyId);
      params.set("interactionKind", this.nextInteractionKind || "RETRY");
      return params;
    }

    navigate(mutate, { resetPage = true, replace = false } = {}) {
      const params = this.params();
      const beforeQuery = params.get("q")?.trim() || "";
      const beforePage = params.get("page") || "1";
      mutate(params);
      if (resetPage) params.set("page", "1");
      const afterQuery = params.get("q")?.trim() || "";
      const afterPage = params.get("page") || "1";
      if (afterQuery !== beforeQuery) {
        this.journeyId = uuid();
        this.nextInteractionKind = "INTENT";
      } else if (afterPage !== beforePage) this.nextInteractionKind = "PAGINATION";
      else this.nextInteractionKind = "REFINEMENT";
      const url = `${location.pathname}?${params.toString()}`;
      history[replace ? "replaceState" : "pushState"](
        { ...(history.state || {}), boiSearch: { journeyId: this.journeyId, query: afterQuery } },
        "",
        url,
      );
      this.load();
    }

    setLoading() {
      this.setAttribute("aria-busy", "true");
      const existing = this.querySelector(".boi-search__shell");
      if (existing) {
        existing.classList.add("is-loading");
        return;
      }
      const loading = element("div", { class: "boi-search__loading", role: "status" });
      loading.append(element("span", { class: "boi-search__loading-label" }, this.t.loading));
      const skeletons = element("div", {
        class: "boi-search__skeleton-grid",
        "aria-hidden": "true",
      });
      for (let index = 0; index < 6; index++)
        skeletons.append(element("span", { class: "boi-search__skeleton-card" }));
      loading.append(skeletons);
      this.replaceChildren(loading);
    }

    showError() {
      const box = element("div", { class: "boi-search__error", role: "alert" });
      box.append(
        element("p", {}, this.dataset.b2b === "true" ? this.t.b2b_unavailable : this.t.error),
      );
      const retry = element("button", { type: "button", class: "boi-search__retry" }, this.t.retry);
      retry.addEventListener("click", () => {
        this.nextInteractionKind = "RETRY";
        this.load();
      });
      box.append(retry);
      this.replaceChildren(box);
      const native =
        this.dataset.nativeResultsSelector &&
        document.querySelector(this.dataset.nativeResultsSelector);
      if (native) native.hidden = false;
    }

    async load() {
      this.abort?.abort();
      this.abort = new AbortController();
      const sequence = ++this.sequence;
      const params = this.apiParams();
      const query = params.get("q");
      if (!query) {
        this.replaceChildren();
        return;
      }
      this.setLoading();
      try {
        const data = await request(this, `/v1/search?${params.toString()}`, this.abort.signal);
        if (sequence !== this.sequence) return;
        const redirect = safePath(data.redirect?.path);
        if (redirect && redirect !== location.pathname) {
          window.location.assign(new URL(redirect, location.origin).href);
          return;
        }
        this.render(data);
        if (!data.total) void this.renderAlternatives(query, sequence);
        const native =
          this.dataset.nativeResultsSelector &&
          document.querySelector(this.dataset.nativeResultsSelector);
        if (native) native.hidden = true;
        this.nextInteractionKind = "RETRY";
      } catch (error) {
        if (error.name !== "AbortError" && sequence === this.sequence) this.showError();
      } finally {
        if (sequence === this.sequence) this.removeAttribute("aria-busy");
      }
    }

    async renderAlternatives(query, sequence) {
      let data;
      try {
        data = await request(
          this,
          `/v1/autocomplete?q=${encodeURIComponent(query)}&locale=${encodeURIComponent(this.dataset.locale)}&marketCountry=${encodeURIComponent(this.dataset.country)}&limit=4`,
          this.abort.signal,
        );
      } catch {
        return;
      }
      if (sequence !== this.sequence || this.abort.signal.aborted) return;
      const labels = (data.suggestions || []).map((item) => item.label).slice(0, 4);
      if (!labels.length) return;
      const box = element("div", { class: "boi-search__dym" });
      box.append(element("span", { class: "boi-search__dym-label" }, this.t.did_you_mean));
      labels.forEach((label, index) => {
        if (index) box.append(element("span", { class: "boi-search__dym-sep" }, "·"));
        const link = element("a", { href: searchUrl(this.dataset.rootUrl, label) }, label);
        link.addEventListener("click", (event) => {
          event.preventDefault();
          this.navigate((params) => params.set("q", label));
        });
        box.append(link);
      });
      this.querySelector(".boi-search__toolbar")?.after(box);
    }

    removeFilter(param, value) {
      this.navigate((params) => {
        const remaining = params.getAll(param).filter((item) => item !== value);
        params.delete(param);
        remaining.forEach((item) => params.append(param, item));
      });
    }

    renderChips(data) {
      const chips = element("div", { class: "boi-search__chips" });
      for (const param of API_FILTER_PARAMS) {
        for (const value of this.params().getAll(param)) {
          const label = facetValueLabel(param, value, this.t, this.dataset.locale);
          const chip = element("span", { class: "boi-search__chip" });
          chip.append(element("span", {}, label));
          const remove = element("button", {
            type: "button",
            class: "boi-search__chip-remove",
            "aria-label": formatTemplate(this.t.remove_filter, { value: label }),
          });
          remove.append(element("span", { "aria-hidden": "true" }, "×"));
          remove.addEventListener("click", () => this.removeFilter(param, value));
          chip.append(remove);
          chips.append(chip);
        }
      }
      const currency = data.results[0]?.price.currency || this.dataset.currency || "EUR";
      for (const [minKey, maxKey, label] of [
        ["priceMin", "priceMax", this.t.facet_price],
        ["alcoholMin", "alcoholMax", this.t.facet_alcohol],
      ]) {
        const min = this.params().get(minKey);
        const max = this.params().get(maxKey);
        if (min === null && max === null) continue;
        const format = (value) =>
          minKey === "priceMin" ? money(Number(value), currency, this.dataset.locale) : `${value}%`;
        const bounds = [min, max]
          .filter((value) => value !== null)
          .map(format)
          .join(" – ");
        const text = `${label}: ${bounds}`;
        const chip = element("span", { class: "boi-search__chip" });
        chip.append(element("span", {}, text));
        const remove = element("button", {
          type: "button",
          class: "boi-search__chip-remove",
          "aria-label": formatTemplate(this.t.remove_filter, { value: text }),
        });
        remove.append(element("span", { "aria-hidden": "true" }, "×"));
        remove.addEventListener("click", () =>
          this.navigate((params) => {
            params.delete(minKey);
            params.delete(maxKey);
          }),
        );
        chip.append(remove);
        chips.append(chip);
      }
      if (!chips.childNodes.length) return null;
      const clear = element(
        "button",
        { type: "button", class: "boi-search__clear" },
        this.t.clear_filters,
      );
      clear.addEventListener("click", () =>
        this.navigate((params) => ALL_FILTER_PARAMS.forEach((key) => params.delete(key))),
      );
      chips.append(clear);
      return chips;
    }

    renderRangeFacet(facet, kind) {
      const isPrice = kind === "price";
      const minKey = isPrice ? "priceMin" : "alcoholMin";
      const maxKey = isPrice ? "priceMax" : "alcoholMax";
      const details = element("details", { class: "boi-search__facet", open: true });
      details.append(element("summary", {}, facetLabel(facet, this.t)));
      const form = element("form", { class: "boi-search__range-form" });
      const min = element("input", {
        type: "number",
        min: facet.range?.min ?? 0,
        max: facet.range?.max,
        step: "any",
        inputMode: "decimal",
        placeholder: isPrice ? this.t.price_min_placeholder : this.t.alcohol_min_placeholder,
        "aria-label": isPrice ? this.t.price_min_placeholder : this.t.alcohol_min_placeholder,
      });
      const max = element("input", {
        type: "number",
        min: facet.range?.min ?? 0,
        max: facet.range?.max,
        step: "any",
        inputMode: "decimal",
        placeholder: isPrice ? this.t.price_max_placeholder : this.t.alcohol_max_placeholder,
        "aria-label": isPrice ? this.t.price_max_placeholder : this.t.alcohol_max_placeholder,
      });
      min.value = this.params().get(minKey) || "";
      max.value = this.params().get(maxKey) || "";
      const apply = element(
        "button",
        { type: "submit", class: "boi-search__range-apply" },
        this.t.apply,
      );
      form.append(min, max, apply);
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (min.value && max.value && Number(min.value) > Number(max.value)) return;
        this.navigate((params) => {
          params.delete(minKey);
          params.delete(maxKey);
          if (min.value) params.set(minKey, min.value);
          if (max.value) params.set(maxKey, max.value);
        });
      });
      details.append(form);
      return details;
    }

    renderFacet(facet, defaultOpen = false) {
      const param = FACET_PARAMS[facet.key];
      if (!param || !facet.values?.length) return null;
      const selectedValues = this.params().getAll(param);
      const details = element("details", {
        class: "boi-search__facet",
        open: defaultOpen || selectedValues.length > 0,
      });
      details.append(element("summary", {}, facetLabel(facet, this.t)));
      const list = element("div", { class: "boi-search__facet-values" });
      const rows = [];
      facet.values.forEach((item, index) => {
        const id = `boi-facet-${uuid()}`;
        const labelText = facetValueLabel(param, item.value, this.t, this.dataset.locale);
        const row = element("label", { class: "boi-search__facet-value", htmlFor: id });
        if (index >= 8 && !selectedValues.includes(item.value)) row.hidden = true;
        const input = element("input", {
          type: "checkbox",
          id,
          value: item.value,
          checked: this.params().getAll(param).includes(item.value),
        });
        input.addEventListener("change", () => {
          this.navigate((params) => {
            const values = params.getAll(param).filter((value) => value !== item.value);
            params.delete(param);
            if (input.checked) values.push(item.value);
            values.forEach((value) => params.append(param, value));
          });
        });
        row.append(input, element("span", {}, labelText), element("small", {}, String(item.count)));
        list.append(row);
        rows.push(row);
      });
      details.append(list);
      if (rows.some((row) => row.hidden)) {
        const toggle = element(
          "button",
          { type: "button", class: "boi-search__facet-toggle", "aria-expanded": "false" },
          this.t.show_more,
        );
        toggle.addEventListener("click", () => {
          const expanded = toggle.getAttribute("aria-expanded") === "true";
          toggle.setAttribute("aria-expanded", String(!expanded));
          rows.forEach((row, index) => {
            row.hidden = expanded && index >= 8 && !row.querySelector("input")?.checked;
          });
          toggle.textContent = expanded ? this.t.show_more : this.t.show_less;
        });
        details.append(toggle);
      }
      return details;
    }

    activeFilterCount() {
      const params = this.params();
      const values = API_FILTER_PARAMS.reduce((count, key) => count + params.getAll(key).length, 0);
      const ranges = [
        ["priceMin", "priceMax"],
        ["alcoholMin", "alcoholMax"],
      ].filter(([min, max]) => params.has(min) || params.has(max)).length;
      return values + ranges;
    }

    openFilters(shell, facets, button) {
      this.filterScrollPosition = window.scrollY;
      shell.dataset.filtersOpen = "true";
      facets.setAttribute("aria-hidden", "false");
      button.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("boi-search-filters-open");
      this.filterTrigger = button;
      facets.querySelector("button")?.focus();
    }

    closeFilters(shell, facets) {
      shell.dataset.filtersOpen = "false";
      facets.setAttribute("aria-hidden", "true");
      this.filterTrigger?.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("boi-search-filters-open");
      this.filterTrigger?.focus({ preventScroll: true });
      if (Number.isFinite(this.filterScrollPosition))
        window.scrollTo({ top: this.filterScrollPosition, behavior: "auto" });
    }

    trapFilterFocus(event, shell, facets) {
      if (shell.dataset.filtersOpen !== "true") return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeFilters(shell, facets);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...facets.querySelectorAll("button,input,summary,[href],select")].filter(
        (node) => !node.disabled && node.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    productCard(product, index, data) {
      const b2b = data.pricingContext?.audience === "b2b";
      const productId = numericShopifyId(product.productId) || String(index + 1);
      const item = element("li", {
        class: `boi-search-card productBox grid__item item-row ${
          product.availability === "out_of_stock" ? "sold-out" : ""
        } ${!b2b && discountPercent(product) ? "on-sale" : ""} prod-grid-view`,
        id: `product-${productId}`,
      });
      const card = element("div", {
        class: "boi-search-card__inner products product-hover-11 custom-collection-card",
      });
      const link = productUrl(this.dataset.rootUrl, product.handle);
      const form = element("form", {
        class: "boi-search-card__form custom-card-form-wrap",
        id: `cart-form-${productId}`,
        action: `${rootPath(this.dataset.rootUrl)}cart/add`,
        method: "post",
      });
      const container = element("div", {
        class: "boi-search-card__layout product-container custom-card-horizontal",
      });
      const left = element("div", { class: "boi-search-card__left custom-card-left" });
      const iconBadges = element("div", { class: "boi-search-card__icon-badges" });
      const tags = (product.tags || []).map((tag) => tag.toLocaleLowerCase("und"));
      const promotion = promotionFor(this, product, b2b);
      const addIconBadge = (symbol, label, modifier) => {
        const badge = element("span", {
          class: `boi-search-card__icon-badge is-${modifier}`,
          title: label,
          "aria-label": label,
        });
        badge.append(
          typeof symbol === "string" && (symbol.startsWith("https://") || symbol.startsWith("//"))
            ? element("img", { src: symbol, alt: "", width: 35, height: 35, loading: "lazy" })
            : symbol,
        );
        iconBadges.append(badge);
      };
      if (tags.includes("premiato"))
        addIconBadge(this.dataset.awardIcon || "🏆", this.t.award, "award is-has-image");
      if (tags.includes("marketplace"))
        addIconBadge(
          "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/marketplace.svg?v=1776413337",
          this.t.marketplace,
          "marketplace is-has-image",
        );
      const inventory = product.selectedVariant?.inventoryQuantity;
      const hasPositiveAvailability =
        typeof inventory === "number" ? inventory > 0 : product.availability === "in_stock";
      if (tags.includes("diretta") && hasPositiveAvailability)
        addIconBadge(
          "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/immediate-availability.svg?v=1753958561",
          this.t.in_stock,
          "shipping is-green is-has-image",
        );
      else if (tags.includes("sped-2-5") && hasPositiveAvailability)
        addIconBadge(
          "https://cdn.shopify.com/s/files/1/0538/2268/5356/files/sped-2-5-availability.svg?v=1753964190",
          this.t.fast_shipping,
          "shipping is-yellow is-has-image",
        );
      if (iconBadges.childNodes.length) left.append(iconBadges);
      const media = element("a", {
        class: "boi-search-card__media custom-card-image-link",
        href: link,
        "aria-label": product.title,
      });
      if (product.imageUrl)
        media.append(
          element("img", {
            class: "featured-image",
            src: product.imageUrl,
            alt: "",
            loading: index < 3 ? "eager" : "lazy",
            decoding: "async",
            width: 130,
            height: 230,
          }),
        );
      left.append(media);
      const details = element("div", { class: "boi-search-card__details custom-card-right" });
      if (product.producer)
        details.append(
          element("span", { class: "boi-search-card__producer product-vendor" }, product.producer),
        );
      const titleHeader = element("div", { class: "custom-card-header-row" });
      const titleLink = element("a", { class: "custom-card-title-link", href: link });
      titleLink.append(
        element(
          "h2",
          { class: "boi-search-card__title custom-card-title grid-link__title" },
          product.title,
        ),
      );
      titleHeader.append(titleLink);
      details.append(titleHeader);
      const metadataParts = [];
      if (product.vintage) metadataParts.push(product.vintage);
      if (product.format) metadataParts.push(formatVolume(product.format, this.dataset.locale));
      if (typeof product.alcoholByVolume === "number")
        metadataParts.push(`${localeNumber(product.alcoholByVolume, this.dataset.locale)}%`);
      if (metadataParts.length)
        details.append(
          element(
            "div",
            { class: "boi-search-card__subtitle custom-card-subtitle" },
            metadataParts.join(" | "),
          ),
        );
      if (promotion?.banner) {
        const promoRow = element("div", { class: "custom-card-top-icons-row" });
        const banner = element(
          "div",
          { class: "custom-card-promo-banner" },
          String(promotion.banner),
        );
        if (promotion.background) banner.style.backgroundColor = String(promotion.background);
        if (promotion.color) banner.style.color = String(promotion.color);
        promoRow.append(banner);
        details.append(promoRow);
      }
      const discount = b2b ? 0 : discountPercent(product);
      const price = element("div", {
        class: "boi-search-card__price custom-card-price-wrap grid-link__meta",
      });
      if (discount)
        price.append(
          element("span", { class: "boi-search-card__discount yousave" }, `-${discount}%`),
        );
      const currentPrice = element("div", { class: "product_price" });
      const promotionalAmount = promotionPrice(Number(product.price.min), promotion, b2b);
      const priceProduct =
        promotion && promotion.type !== "case_quantity_discount"
          ? { ...product, price: { ...product.price, min: promotionalAmount } }
          : product;
      appendPriceDisplay(currentPrice, this, data, priceProduct, "grid-link__org_price");
      price.append(currentPrice);
      if (promotion && promotion.type !== "case_quantity_discount")
        price.append(
          element(
            "del",
            { class: "grid-link__sale_price" },
            money(product.price.min, product.price.currency, this.dataset.locale),
          ),
        );
      if (!b2b && product.price.compareAtMin > product.price.min)
        price.append(
          element(
            "del",
            { class: "grid-link__sale_price" },
            money(product.price.compareAtMin, product.price.currency, this.dataset.locale),
          ),
        );
      details.append(price);
      for (const badge of product.badges || [])
        if (!["Backorder", "Out of stock"].includes(badge))
          details.append(element("span", { class: "boi-search-card__badge" }, badge));
      let cta;
      const knownDeniedInventoryUnavailable =
        product.selectedVariant?.inventoryPolicy === "DENY" &&
        typeof inventory === "number" &&
        inventory <= 0;
      const safeQuickAdd =
        product.availability !== "out_of_stock" &&
        product.variantId &&
        product.selectedVariant?.availableForSale === true &&
        !knownDeniedInventoryUnavailable;
      if (safeQuickAdd) {
        form.append(element("input", { type: "hidden", name: "id", value: product.variantId }));
        const hiddenQuantity = element("input", {
          type: "hidden",
          name: "quantity",
          value: 1,
          class: "card-quantity-hidden",
        });
        form.append(hiddenQuantity);
        const leftActions = element("div", { class: "custom-card-left-actions" });
        const quantityWrap = element("div", { class: "custom-card-left-qty" });
        const widget = element("div", {
          class: "qty-product qty-product--collection",
          dataset: { inventory: typeof inventory === "number" ? String(inventory) : "" },
        });
        const inventoryLimit =
          product.selectedVariant?.inventoryPolicy === "DENY" &&
          typeof inventory === "number" &&
          inventory >= 0
            ? inventory
            : Number.POSITIVE_INFINITY;
        const inputWrapper = element("div", { class: "qty-input-wrapper" });
        const quantity = element("input", {
          type: "number",
          class: "qty-input",
          min: 1,
          max: Number.isFinite(inventoryLimit) ? inventoryLimit : undefined,
          value: 1,
          readOnly: true,
          "aria-label": `${this.t.quantity}: ${product.title}`,
        });
        inputWrapper.append(
          quantity,
          element("div", { class: "qty-toggle", "aria-hidden": "true" }, "▼"),
        );
        const quantityGrid = element("div", { class: "qty-grid" });
        const closeQuantity = () => {
          quantityGrid.classList.remove("open");
          widget.classList.remove("qty-open");
        };
        const setQuantity = (value) => {
          const safe = Math.max(1, Math.min(value, inventoryLimit));
          quantity.value = String(safe);
          hiddenQuantity.value = String(safe);
          hiddenQuantity.setAttribute("value", String(safe));
          quantityGrid
            .querySelectorAll(".qty-option")
            .forEach((option) =>
              option.classList.toggle("selected", Number(option.dataset.value) === safe),
            );
        };
        for (const value of [1, 2, 3, 4, 6, 12, 18, 24]) {
          const disabled = value > inventoryLimit;
          const option = element(
            "button",
            {
              type: "button",
              class: `qty-option${value === 1 ? " selected" : ""}${disabled ? " disabled" : ""}`,
              dataset: { value: String(value) },
              disabled,
            },
            String(value),
          );
          option.addEventListener("click", () => {
            setQuantity(value);
            closeQuantity();
          });
          quantityGrid.append(option);
        }
        const otherLabels = { it: "Altro", de: "Andere", fr: "Autre", en: "Other" };
        const other = element(
          "button",
          { type: "button", class: "qty-altro" },
          otherLabels[this.dataset.locale] || otherLabels.en,
        );
        other.addEventListener("click", () => {
          closeQuantity();
          quantity.readOnly = false;
          quantity.focus();
          quantity.select();
        });
        quantity.addEventListener("change", () => setQuantity(Number(quantity.value) || 1));
        inputWrapper.addEventListener("click", () => {
          const opening = !quantityGrid.classList.contains("open");
          this.querySelectorAll(".qty-grid.open").forEach((grid) => grid.classList.remove("open"));
          this.querySelectorAll(".qty-product--collection.qty-open").forEach((item) =>
            item.classList.remove("qty-open"),
          );
          quantityGrid.classList.toggle("open", opening);
          widget.classList.toggle("qty-open", opening);
        });
        quantityGrid.append(other);
        widget.append(inputWrapper, quantityGrid, element("div", { class: "qty-error" }));
        quantityWrap.append(widget);
        leftActions.append(quantityWrap, element("div", { class: "custom-low-stock-slot-spacer" }));
        left.append(leftActions);
        const rightActions = element("div", { class: "custom-card-right-actions" });
        const buttonWrap = element("div", { class: "custom-card-right-bottom" });
        cta = element(
          "button",
          {
            type: "submit",
            name: "add",
            class: "boi-search-card__cta btn atc-main-btn custom-card-add-btn",
          },
          this.t.add_to_cart,
        );
        buttonWrap.append(cta);
        rightActions.append(buttonWrap, element("div", { class: "custom-low-stock-slot" }));
        details.append(rightActions);
      } else if (product.availability === "out_of_stock") {
        details.append(
          element(
            "div",
            { class: "boi-search-card__sold-out custom-card-sold-out-wrap" },
            this.t.sold_out,
          ),
        );
      } else {
        cta = element(
          "a",
          { class: "boi-search-card__cta custom-card-add-btn", href: link },
          this.t.view_product,
        );
        details.append(cta);
      }
      if (cta?.tagName === "BUTTON")
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          const quantity = Number(form.querySelector(".qty-input")?.value || 1);
          const hiddenInput = form.querySelector(".card-quantity-hidden");
          if (hiddenInput) {
            hiddenInput.value = String(quantity);
            hiddenInput.setAttribute("value", String(quantity));
          }
          cta.disabled = true;
          let added = false;
          try {
            const addResponse = await fetch(`${rootPath(this.dataset.rootUrl)}cart/add.js`, {
              method: "POST",
              credentials: "same-origin",
              headers: { "content-type": "application/json", accept: "application/json" },
              body: JSON.stringify({ items: [{ id: product.variantId, quantity }] }),
            });
            if (!addResponse.ok) throw new Error(`Cart add ${addResponse.status}`);
            added = true;
            directQuickAddVariants.add(String(product.variantId));
            const cartResponse = await fetch(`${rootPath(this.dataset.rootUrl)}cart.js`, {
              credentials: "same-origin",
              headers: { accept: "application/json" },
            });
            if (!cartResponse.ok) throw new Error(`Cart ${cartResponse.status}`);
            const cart = await cartResponse.json();
            document.dispatchEvent(
              new CustomEvent("bottle-search:cart-added", { detail: { cart } }),
            );
            if (cart.token && data.analyticsToken && data.searchId)
              void fetch(`${this.dataset.apiBase.replace(/\/$/, "")}/v1/analytics/cart`, {
                method: "POST",
                credentials: "omit",
                keepalive: true,
                headers: { "content-type": "application/json", "x-search-session": sessionId },
                body: JSON.stringify({
                  eventId: uuid(),
                  searchId: data.searchId,
                  analyticsToken: data.analyticsToken,
                  cartToken: cart.token,
                  productId: product.productId,
                  variantId: String(product.variantId),
                  quantity,
                  attributionSource: "DIRECT_QUICK_ADD",
                }),
              }).catch(() => {});
          } catch {
            if (!added) form.submit();
          } finally {
            cta.disabled = false;
          }
        });
      for (const clickable of [media, titleLink, cta].filter(Boolean))
        clickable.addEventListener(
          "click",
          () =>
            clickable.tagName === "A" &&
            sendProductClick(this.dataset.apiBase, data, product, index + 1, "results"),
        );
      container.append(left, details);
      form.append(container);
      card.append(form);
      item.append(card);
      return item;
    }

    renderPagination(data) {
      if (data.totalPages <= 1) return null;
      const nav = element("nav", {
        class: "boi-search__pagination",
        "aria-label": this.t.pagination_aria,
      });
      const addPage = (page, label, className) => {
        const button = element(
          "button",
          {
            type: "button",
            class: className,
            disabled: page < 1 || page > data.totalPages,
            "aria-current": page === data.page ? "page" : null,
            "aria-label": label,
          },
          className ? label : String(page),
        );
        button.addEventListener("click", () => {
          this.navigate((params) => params.set("page", String(page)), { resetPage: false });
          scrollTo({ top: Math.max(0, this.offsetTop - 120), behavior: "smooth" });
        });
        nav.append(button);
      };
      addPage(data.page - 1, `← ${this.t.previous_page}`, "boi-search__pagination-edge");
      for (
        let page = Math.max(1, data.page - 2);
        page <= Math.min(data.totalPages, data.page + 2);
        page++
      )
        addPage(page, `${this.t.pagination_aria} ${page}`, "");
      addPage(data.page + 1, `${this.t.next_page} →`, "boi-search__pagination-edge");
      return nav;
    }

    render(data) {
      document.documentElement.classList.remove("boi-search-filters-open");
      const shell = element("div", {
        class: "boi-search__shell",
        dataset: { filtersOpen: "false" },
      });
      const facets = element("aside", {
        class: "boi-search__facets",
        "aria-label": this.t.facets_aria,
        "aria-hidden": "false",
      });
      const facetHeader = element("div", { class: "boi-search__facets-header" });
      const activeCount = this.activeFilterCount();
      const facetTitle = element("h2", {}, this.t.filter_results);
      if (activeCount)
        facetTitle.append(
          element("span", { class: "boi-search__facets-count" }, String(activeCount)),
        );
      facetHeader.append(facetTitle);
      const close = element("button", {
        type: "button",
        class: "boi-search__facets-close",
        "aria-label": this.t.close,
      });
      close.append(element("span", { "aria-hidden": "true" }, "×"));
      close.addEventListener("click", () => this.closeFilters(shell, facets));
      facetHeader.append(close);
      facets.append(facetHeader);
      let facetIndex = 0;
      for (const facet of data.facets || []) {
        if (facet.key === "category") continue;
        let rendered = null;
        if (facet.key === "price" && facet.type === "range")
          rendered = this.renderRangeFacet(facet, "price");
        else if (facet.key === "alcohol_by_volume") {
          let alcoholFacet = facet;
          if (facet.type === "values") {
            const values = facet.values
              ?.map((item) => Number(item.value))
              .filter((value) => Number.isFinite(value));
            if (values?.length)
              alcoholFacet = {
                ...facet,
                type: "range",
                range: { min: Math.min(...values), max: Math.max(...values) },
              };
          }
          if (alcoholFacet.type === "range")
            rendered = this.renderRangeFacet(alcoholFacet, "alcohol");
        } else if (facet.type === "values") rendered = this.renderFacet(facet, facetIndex < 3);
        if (rendered) {
          facets.append(rendered);
          facetIndex++;
        }
      }
      const facetFooter = element("div", { class: "boi-search__facets-footer" });
      if (activeCount) {
        const clear = element(
          "button",
          { type: "button", class: "boi-search__facets-clear" },
          this.t.clear_filters,
        );
        clear.addEventListener("click", () =>
          this.navigate((params) => ALL_FILTER_PARAMS.forEach((key) => params.delete(key))),
        );
        facetFooter.append(clear);
      }
      const showResults = element(
        "button",
        { type: "button", class: "boi-search__facets-apply" },
        formatTemplate(this.t.show_results_template, { count: data.total }),
      );
      showResults.addEventListener("click", () => this.closeFilters(shell, facets));
      facetFooter.append(showResults);
      facets.append(facetFooter);
      const main = element("section", { class: "boi-search__main" });
      const toolbar = element("div", { class: "boi-search__toolbar" });
      const filterButton = element(
        "button",
        { type: "button", class: "boi-search__mobile-filters", "aria-expanded": "false" },
        activeCount
          ? formatTemplate(this.t.filter_count_template, { count: activeCount })
          : this.t.filters,
      );
      filterButton.addEventListener("click", () => this.openFilters(shell, facets, filterButton));
      const status = element(
        "div",
        { class: "boi-search__status", role: "status", "aria-live": "polite" },
        formatTemplate(this.t.status_template, { count: data.total, query: data.query }),
      );
      const sortWrap = element("label", { class: "boi-search__sort" });
      sortWrap.append(element("span", {}, this.t.sort));
      const sort = element("select", { "aria-label": this.t.sort });
      for (const [value, label] of [
        ["relevance", this.t.sort_relevance],
        ["price_asc", this.t.sort_price_asc],
        ["price_desc", this.t.sort_price_desc],
        ["newest", this.t.sort_newest],
      ])
        sort.append(element("option", { value, selected: data.sort === value }, label));
      sort.addEventListener("change", () =>
        this.navigate((params) => params.set("sort", sort.value)),
      );
      sortWrap.append(sort);
      toolbar.append(filterButton, status, sortWrap);
      main.append(toolbar);
      const chips = this.renderChips(data);
      if (chips) main.append(chips);
      const grid = element("ul", { class: "boi-search__grid products-grid-view" });
      data.results.forEach((product, index) => grid.append(this.productCard(product, index, data)));
      main.append(grid);
      if (!data.total)
        main.append(
          element(
            "div",
            { class: "boi-search__empty", role: "status" },
            `${this.t.no_results} “${data.query}”`,
          ),
        );
      const pagination = this.renderPagination(data);
      if (pagination) main.append(pagination);
      shell.addEventListener("keydown", (event) => this.trapFilterFocus(event, shell, facets));
      shell.append(facets, main);
      this.replaceChildren(shell);
    }
  }

  if (!customElements.get("bottle-search-autocomplete"))
    customElements.define("bottle-search-autocomplete", BottleAutocomplete);
  if (!customElements.get("bottle-search-results"))
    customElements.define("bottle-search-results", BottleResults);

  const installProductPageAttribution = async () => {
    const host = document.querySelector("bottle-search-results,bottle-search-autocomplete");
    const apiBase = host?.dataset.apiBase;
    if (!apiBase) return;
    let quantities;
    try {
      const response = await fetch(`${rootPath(window.Shopify?.routes?.root || "/")}cart.js`, {
        credentials: "same-origin",
        headers: { accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      quantities = new Map(
        (cart.items || []).map((item) => [
          String(item.variant_id || item.id),
          Number(item.quantity),
        ]),
      );
    } catch {
      return;
    }
    document.addEventListener("cart:updated", (event) => {
      const cart = event.detail?.cart || event.detail;
      if (!cart?.token || !Array.isArray(cart.items)) return;
      const next = new Map(
        cart.items.map((item) => [String(item.variant_id || item.id), Number(item.quantity)]),
      );
      const records = attributionRecords();
      for (const item of cart.items) {
        const variantId = String(item.variant_id || item.id);
        const addedQuantity = Number(item.quantity) - (quantities.get(variantId) || 0);
        if (addedQuantity <= 0) continue;
        if (directQuickAddVariants.delete(variantId)) continue;
        const record = [...records]
          .reverse()
          .find((candidate) => String(candidate.productNumericId) === String(item.product_id));
        if (!record) continue;
        void fetch(`${apiBase.replace(/\/$/, "")}/v1/analytics/cart`, {
          method: "POST",
          credentials: "omit",
          keepalive: true,
          headers: { "content-type": "application/json", "x-search-session": sessionId },
          body: JSON.stringify({
            eventId: record.eventId,
            searchId: record.searchId,
            analyticsToken: record.attributionToken,
            attributionToken: record.attributionToken,
            attributionSource: "PRODUCT_PAGE",
            cartToken: cart.token,
            productId: record.productId,
            variantId,
            quantity: addedQuantity,
          }),
        })
          .then((response) => {
            if (!response.ok) return;
            try {
              localStorage.setItem(
                ATTRIBUTION_STORAGE_KEY,
                JSON.stringify(
                  attributionRecords().filter((candidate) => candidate.eventId !== record.eventId),
                ),
              );
            } catch {
              // The stable event ID keeps retries idempotent if storage cannot be updated.
            }
          })
          .catch(() => undefined);
      }
      quantities = next;
    });
  };
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", () => void installProductPageAttribution(), {
      once: true,
    });
  else void installProductPageAttribution();
  document.documentElement.dataset.boiSearchRuntime = "ready";
})();
